import {
  paymentPurposeLabels,
  paymentReturnPaths,
  paymentStatusLabels,
} from "@car/api/payments/config";
import { Button } from "@car/ui/components/button";
import { Card, CardContent } from "@car/ui/components/card";
import { Separator } from "@car/ui/components/separator";
import { Skeleton } from "@car/ui/components/skeleton";
import { Spinner } from "@car/ui/components/spinner";
import {
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  PrinterIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { z } from "zod";

import { faDateTimeShort, faNumber, faToman, toFaDigits } from "@/lib/format";
import { gatewayPostReturn } from "@/lib/gateway-return";
import { orpc } from "@/utils/orpc";

// بازگشت از درگاه: پارامترهای بازگشت همان‌طور که آمده‌اند برای تأیید سمت سرور فرستاده می‌شوند؛
// نتیجه فقط از پاسخ سرور خوانده می‌شود، نه از خود نشانی.
export const Route = createFileRoute("/pay/callback/$paymentId")({
  // پارامترهای درگاه متن‌اند؛ مقداری که روتر عدد خوانده (مثلاً کد تراکنش) دوباره متن می‌شود
  validateSearch: z.record(z.string(), z.coerce.string()),
  server: { handlers: { POST: gatewayPostReturn } },
  head: () => ({ meta: [{ title: "نتیجه پرداخت | تابان خودرو" }] }),
  component: PaymentResultPage,
});

const backLabels = {
  service_invoice: "بازگشت به سوابق و فاکتورها",
  parts_quote: "بازگشت به درخواست‌ها",
  vehicle_order: "بازگشت به سفارش‌ها",
  installment: "بازگشت به اقساط",
} as const;

function PaymentResultPage() {
  const { paymentId } = Route.useParams();
  const params = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasParams = Object.keys(params).length > 0;

  const verify = useMutation(
    orpc.panel.payments.verify.mutationOptions({
      onSuccess: (result) => {
        queryClient.invalidateQueries();
        // نتیجه قطعی شد: پارامترهای درگاه از نشانی برداشته می‌شوند. خطای گذرا (سرور یا بانک در
        // دسترس نبود) آن‌ها را نگه می‌دارد تا بارگذاری دوباره صفحه، تأیید را دوباره امتحان کند.
        if (result.ok) {
          navigate({
            to: "/pay/callback/$paymentId",
            params: { paymentId },
            search: {},
            replace: true,
          });
        }
      },
    }),
  );
  const sent = useRef(false);
  useEffect(() => {
    if (!hasParams || sent.current) return;
    sent.current = true;
    verify.mutate({ paymentId, params });
  }, [hasParams, paymentId, params, verify]);

  const query = useQuery({
    ...orpc.panel.payments.get.queryOptions({ input: { paymentId } }),
    // بعد از تأیید (یا بدون پارامتر درگاه، مثلاً پرداخت کامل با کیف پول) وضعیت ذخیره‌شده خوانده می‌شود
    enabled: !hasParams || verify.isSuccess || verify.isError,
  });

  const verified = verify.data?.ok ? verify.data : null;
  const record = verified?.payment ?? query.data ?? null;
  const busy = hasParams && !verify.isSuccess && !verify.isError;

  if (busy || (!record && query.isPending)) {
    return (
      <Card>
        <CardContent className="grid justify-items-center gap-3 py-10 text-center">
          <Spinner className="size-6" />
          <p className="font-bold">در حال بررسی پرداخت…</p>
          <Skeleton className="h-4 w-48" />
        </CardContent>
      </Card>
    );
  }

  if (!record) {
    return (
      <Card>
        <CardContent className="grid justify-items-center gap-3 py-10 text-center">
          <p className="font-bold">
            {verify.data && !verify.data.ok ? verify.data.reason : "این پرداخت پیدا نشد."}
          </p>
          <Button nativeButton={false} render={<Link to="/panel" />}>
            بازگشت به پنل
          </Button>
        </CardContent>
      </Card>
    );
  }

  const succeeded = record.status === "succeeded";
  // در انتظار درگاه یا در انتظار تأیید کارشناس (واریز اعلام‌شده، چک)
  const pending = record.status === "pending" || record.status === "review";
  const backTo = paymentReturnPaths[record.purpose];

  return (
    <Card className="print:border-0 print:shadow-none">
      <CardContent className="grid gap-5 py-2">
        <div className="grid justify-items-center gap-2 text-center">
          <span
            className={
              succeeded
                ? "grid size-14 place-items-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                : pending
                  ? "grid size-14 place-items-center rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-400"
                  : "grid size-14 place-items-center rounded-full bg-destructive/10 text-destructive"
            }
          >
            <HugeiconsIcon
              icon={succeeded ? CheckmarkCircle02Icon : pending ? Clock01Icon : CancelCircleIcon}
              strokeWidth={1.8}
              className="size-8"
            />
          </span>
          <h1 className="text-lg font-black">
            {succeeded
              ? "پرداخت با موفقیت انجام شد"
              : record.status === "review"
                ? "پرداخت شما ثبت شد و در انتظار تأیید است"
                : pending
                  ? "پرداخت هنوز تکمیل نشده است"
                  : record.status === "refunded"
                    ? "این پرداخت بازگشت داده شد"
                    : record.status === "rejected"
                      ? "پرداخت اعلام‌شده تأیید نشد"
                      : "پرداخت انجام نشد"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {succeeded
              ? `${paymentPurposeLabels[record.purpose]} ثبت شد.`
              : record.status === "review"
                ? "پس از تطبیق با حساب، نتیجه با پیامک و در پنل اعلام می‌شود."
                : pending
                  ? verify.data && !verify.data.ok
                    ? verify.data.reason
                    : "اگر در درگاه پرداخت کرده‌اید، چند لحظه بعد دوباره این صفحه را باز کنید."
                  : record.failureReason || "پرداخت تکمیل نشد."}
          </p>
          {/* کد پیگیری روی پرداخت ناموفق یعنی بانک پول را گرفته و پیگیری با همکاران است، نه بازگشت خودکار */}
          {(record.status === "failed" || record.status === "cancelled") &&
          record.gatewayAmount > 0 &&
          !record.sandbox &&
          !record.reference ? (
            <p className="text-xs text-muted-foreground">
              اگر مبلغی از حساب شما کسر شده باشد، بانک حداکثر تا ۷۲ ساعت آن را بازمی‌گرداند.
            </p>
          ) : null}
        </div>

        {record.sandbox ? (
          <p className="rounded-lg bg-amber-500/15 p-3 text-center text-sm font-bold text-amber-800 dark:text-amber-400">
            پرداخت آزمایشی: درگاه در حالت آزمایشی بود و هیچ مبلغی از کارت شما برداشت نشد.
          </p>
        ) : null}

        <dl className="grid gap-2 rounded-lg bg-muted/60 p-4 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">مبلغ کل</dt>
            <dd className="font-bold">{faToman(record.amount)}</dd>
          </div>
          {record.walletAmount > 0 ? (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">از کیف پول</dt>
              <dd>{faToman(record.walletAmount)}</dd>
            </div>
          ) : null}
          {record.gatewayAmount > 0 ? (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{record.providerLabel}</dt>
              <dd>{faToman(record.gatewayAmount)}</dd>
            </div>
          ) : null}
          <Separator />
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">وضعیت</dt>
            <dd className="font-bold">{paymentStatusLabels[record.status]}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">تاریخ</dt>
            <dd>{faDateTimeShort(record.paidIso)}</dd>
          </div>
          {record.reference && record.status !== "pending" ? (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">کد پیگیری</dt>
              <dd dir="ltr" className="font-mono font-bold">
                {toFaDigits(record.reference)}
              </dd>
            </div>
          ) : null}
          {record.cardPan ? (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">شماره کارت</dt>
              <dd dir="ltr" className="font-mono">
                {toFaDigits(record.cardPan)}
              </dd>
            </div>
          ) : null}
          {record.chequeNumber ? (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">چک</dt>
              <dd>
                <span dir="ltr">{toFaDigits(record.chequeNumber)}</span> {record.chequeBank}
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">شناسه پرداخت</dt>
            <dd dir="ltr" className="font-mono text-xs">
              {record.id.slice(4, 12).toUpperCase()}
            </dd>
          </div>
        </dl>

        {verified && (verified.points > 0 || verified.cashback > 0) ? (
          <p className="rounded-lg bg-primary/10 p-3 text-center text-sm font-bold text-primary">
            {[
              verified.points > 0 ? `${faNumber(verified.points)} امتیاز باشگاه` : "",
              verified.cashback > 0 ? `${faToman(verified.cashback)} بازگشت وجه به کیف پول` : "",
            ]
              .filter(Boolean)
              .join(" و ")}{" "}
            گرفتید.
          </p>
        ) : null}

        <div className="grid gap-2 sm:grid-cols-2 print:hidden">
          {/* بانک هنوز پاسخ نداده: همین بازگشت دوباره بررسی می‌شود، بدون پرداخت دوباره */}
          {record.status === "pending" && hasParams ? (
            <Button
              disabled={verify.isPending}
              onClick={() => verify.mutate({ paymentId, params })}
            >
              {verify.isPending ? <Spinner className="size-4" /> : null}
              بررسی دوباره
            </Button>
          ) : null}
          <Button
            variant={record.status === "pending" && hasParams ? "outline" : "default"}
            nativeButton={false}
            render={<Link to={backTo} />}
          >
            {record.status === "failed" || record.status === "cancelled"
              ? "تلاش دوباره"
              : backLabels[record.purpose]}
          </Button>
          {succeeded || record.status === "review" ? (
            <Button variant="outline" onClick={() => window.print()}>
              <HugeiconsIcon icon={PrinterIcon} strokeWidth={1.8} />
              چاپ رسید
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
